import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { FiMaximize, FiHome, FiDroplet, FiTruck, FiLayers, FiImage, FiMapPin, FiShare2, FiCheck, FiArrowRight, FiPackage, FiPlay, FiMail, FiLink, FiX } from 'react-icons/fi';
import { FaWhatsapp, FaFacebookF, FaXTwitter, FaLinkedinIn } from 'react-icons/fa6';
import { TbBuildingSkyscraper } from 'react-icons/tb';
import {
  PageContainer, Container, DetailGrid, CTASidebar,
  PriceDisplay, Breadcrumbs,
  HeadingSerif, BodyText, PreTitle, ButtonSolid, ButtonOutline,
  Section, PropertyGrid, MapWrapper,
  DetailHero, DetailHeroInfo, DetailHeroTitle, DetailHeroPrice, DetailHeroCtas,
  GalleryBlock, GalleryMosaic, GalleryShare, ShareToast, MosaicBig, MosaicPills, MosaicPill, MosaicNav, GalleryNavRow,
  MobileHero, MobileHeroTrack, MobileHeroSlide, MobileHeroGradient, MobileHeroTitle, MobileHeroPills,
  MobileHeroDots, MobileHeroDot, MobileHeroCounter,
  MobilePriceCard, MobilePriceCardTitle, MobilePriceLine,
  LightboxOverlay, LightboxHeader, LightboxContent, LightboxBody, LightboxNav,
  LightboxThumbs, LightboxThumb,
  PublishedMeta,
  AddressCard, AddressCardContent, AddressMapBg, SpecsGrid, SpecCell, CodeMetaRow, CodeChip, MetaInline,
  DescriptionBlock, AmenityList, AmenityItem, ValuesTable, ValuesRow,
  SideActions, SideActionButton,
  FloorsAccordion, Floor, FloorHead, FloorBody, FloorPlanImg, FloorDesc, FloorPlanLightbox,
  MortgageBox, MortgageGrid, MortgageField, MortgageResult, MortgageSide,
  SectionTitle, OverviewGrid, OverviewCell,
  AddressV2, AddressV2Head, AddressV2MapBtn, AddressV2Grid,
  DetailsBox, DetailsRow,
  FeaturesGrid3, FeatureItem,
  ListingBadges, ListingBadge,
  ListingHeader, ListingPre, ListingTitle, ListingAddress,
  MosaicSmall, MosaicBigSpan, MosaicMore,
} from './styles/MFRealStateStyles';
import Navbar from './components/Navbar';
import PropertyCard from './components/PropertyCard';
import PropertyMap from './components/PropertyMap';
import AddressMiniMap from './components/AddressMiniMap';
import NearbyPlaces from './components/NearbyPlaces';
import { fetchNearbyPlaces } from './services/nearbyPlacesService';
import { getPropertyCoords } from './services/coordinatesHelper';
import Footer from './components/Footer';
import {
  formatPrice, formatPriceOrConsult, formatArea, formatPublishedAgo,
  getPropertyById, getPublishedProperties,
  generateSeoTitle, generateSeoDescription, generateSeoKeywords,
  buildPropertySlug, optimizedImageUrl,
} from './services/propertyService';
import { getCorretoresDoImovel } from './services/imovelCorretoresService';
import { supabase } from '../../services/supabaseClient';
import { MF_BASE } from './basePath';

const PLACEHOLDER_IMG = 'data:image/svg+xml,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500">
    <rect fill="#E7E9EC" width="800" height="500"/>
    <text x="50%" y="50%" font-family="sans-serif" font-size="20" fill="#A0A6B2" text-anchor="middle" dy=".3em">Sem imagem</text>
  </svg>`
);

const VIDEO_EXTS = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const isVideo = (url) => VIDEO_EXTS.test(url || '');

function NegocioTabs({ activeTab, onChange }) {
  const baseStyle = {
    flex: 1,
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'all 160ms ease',
  };
  const activeStyle = {
    ...baseStyle,
    background: '#0F1828',
    color: '#fff',
    borderRadius: 8,
  };
  const inactiveStyle = {
    ...baseStyle,
    color: '#5C6473',
  };
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        background: '#F1F3F6',
        borderRadius: 10,
        marginBottom: 14,
      }}
    >
      <button
        type="button"
        style={activeTab === 'venda' ? activeStyle : inactiveStyle}
        onClick={() => onChange('venda')}
      >
        Venda
      </button>
      <button
        type="button"
        style={activeTab === 'aluguel' ? activeStyle : inactiveStyle}
        onClick={() => onChange('aluguel')}
      >
        Aluguel
      </button>
    </div>
  );
}

function PlayOverlay() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'rgba(15, 24, 40, 0.72)',
          backdropFilter: 'blur(6px)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
        }}
      >
        <FiPlay size={28} style={{ marginLeft: 4 }} />
      </div>
    </div>
  );
}

function Media({ src, alt, controls = false, draggable, optimizeWidth }) {
  if (isVideo(src)) {
    return (
      <>
        <video
          src={controls ? src : `${src}#t=0.1`}
          muted={!controls}
          autoPlay={controls}
          playsInline
          preload="metadata"
          controls={controls}
        />
        {!controls && <PlayOverlay />}
      </>
    );
  }
  const imgSrc = optimizeWidth ? optimizedImageUrl(src, { width: optimizeWidth }) : src;
  return (
    <img
      src={imgSrc}
      alt={alt || ''}
      onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
      draggable={draggable}
    />
  );
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadPhone, setLeadPhone] = useState('');

  // Carrossel do mosaico desktop: indice do primeiro slot do grupo de 3
  const [mosaicStart, setMosaicStart] = useState(0);
  // Carrossel mobile: indice da foto ativa
  const [mobileIdx, setMobileIdx] = useState(0);
  // Lightbox fullscreen
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Toast "Link copiado"
  const [shareToast, setShareToast] = useState(false);
  // Modal de compartilhamento
  const [shareOpen, setShareOpen] = useState(false);
  // Corretor vinculado ao imovel (busca pelos dados do cadastro se houver corretor_id)
  const [corretorDoImovel, setCorretorDoImovel] = useState(null);
  // Descrição expandida
  const [descExpanded, setDescExpanded] = useState(false);
  const [negocioTab, setNegocioTab] = useState('venda');
  // Plantas baixas (accordion)
  const [openFloor, setOpenFloor] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  // Calculadora de financiamento
  const [mortgage, setMortgage] = useState({ preco: '', entrada: '', meses: '360', juros: '11' });
  const setMortgageField = (k, v) => setMortgage(m => ({ ...m, [k]: v }));
  const formatMoneyInput = (raw) => {
    const n = Number(String(raw || '').replace(/\D/g, ''));
    if (!n) return '';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
  };
  const onMoneyChange = (k) => (e) => setMortgageField(k, formatMoneyInput(e.target.value));

  const mobileTrackRef = useRef(null);
  const mobileScrollDebounce = useRef(null);

  const handleMobileScroll = useCallback(() => {
    const el = mobileTrackRef.current;
    if (!el) return;
    clearTimeout(mobileScrollDebounce.current);
    mobileScrollDebounce.current = setTimeout(() => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setMobileIdx(idx);
    }, 80);
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const prop = await getPropertyById(id);
      setProperty(prop);
      if (prop) {
        // Redirect UUID puro -> slug canônico (preserva SEO)
        const canonicalSlug = buildPropertySlug(prop);
        if (canonicalSlug && id !== canonicalSlug) {
          navigate(`${MF_BASE}/imoveis/${canonicalSlug}`, { replace: true });
        }
        const all = await getPublishedProperties({ cidade: prop.cidade });
        setSimilar(all.filter(p => p.id !== prop.id).slice(0, 3));
      }
      setLoading(false);
    }
    load();
    setMosaicStart(0);
    setMobileIdx(0);
    setLightboxIdx(null);
  }, [id, navigate]);

  useEffect(() => {
    supabase.from('mf_config').select('telefone_encaminhamento_leads').eq('id', 1).single()
      .then(({ data }) => {
        if (data?.telefone_encaminhamento_leads) setLeadPhone(data.telefone_encaminhamento_leads);
      });
  }, []);

  // SEO meta tags + JSON-LD RealEstateListing
  useEffect(() => {
    if (!property) return;

    const seoTitle = property.seo_title || generateSeoTitle(property);
    const seoDesc = property.seo_description || generateSeoDescription(property);
    const seoKw = property.seo_keywords || generateSeoKeywords(property);
    const cover = property.imagens?.find(u => !/\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i.test(u)) || '';
    const canonical = `https://www.mfrealstate.com.br${MF_BASE}/imoveis/${buildPropertySlug(property)}`;

    document.title = seoTitle;

    const setMeta = (name, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', name);
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };
    const setProp = (prop, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[property="${prop}"]`);
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('property', prop);
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };

    setMeta('description', seoDesc);
    if (seoKw) setMeta('keywords', seoKw);

    setProp('og:title', seoTitle);
    setProp('og:description', seoDesc);
    setProp('og:type', 'website');
    setProp('og:url', canonical);
    if (cover) setProp('og:image', cover);
    setProp('og:site_name', 'MF Real Estate');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seoTitle);
    setMeta('twitter:description', seoDesc);
    if (cover) setMeta('twitter:image', cover);

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    // JSON-LD RealEstateListing
    const existing = document.getElementById('property-jsonld');
    if (existing) existing.remove();
    const offers = [];
    if (Number(property.preco) > 0) {
      offers.push({
        '@type': 'Offer',
        price: property.preco,
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
        category: 'sale',
      });
    }
    if (Number(property.preco_aluguel) > 0) {
      offers.push({
        '@type': 'Offer',
        price: property.preco_aluguel,
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
        category: 'rental',
      });
    }
    const jsonld = {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: property.titulo,
      description: seoDesc,
      url: canonical,
      image: cover || undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: property.endereco || undefined,
        addressLocality: property.cidade || undefined,
        addressRegion: 'SP',
        addressCountry: 'BR',
      },
      numberOfRooms: property.quartos || undefined,
      numberOfBathroomsTotal: property.banheiros || undefined,
      floorSize: property.area_m2 ? { '@type': 'QuantitativeValue', value: property.area_m2, unitCode: 'MTK' } : undefined,
      offers: offers.length ? offers : undefined,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'property-jsonld';
    script.textContent = JSON.stringify(jsonld);
    document.head.appendChild(script);
  }, [property]);

  const images = useMemo(() => {
    const all = property?.imagens?.length > 0 ? [...property.imagens] : [PLACEHOLDER_IMG];
    all.sort((a, b) => {
      const aVid = isVideo(a) ? 1 : 0;
      const bVid = isVideo(b) ? 1 : 0;
      return aVid - bVid;
    });
    return all;
  }, [property]);

  const canGoPrev = mosaicStart > 0;
  const canGoNext = mosaicStart + 2 < images.length;

  const handleMosaicPrev = () => setMosaicStart(s => Math.max(0, s - 2));
  const handleMosaicNext = () => setMosaicStart(s => (s + 2 < images.length ? s + 2 : s));

  const openLightbox = useCallback((idx) => setLightboxIdx(idx), []);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);

  // Keyboard nav no lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i < images.length - 1 ? i + 1 : i));
      if (e.key === 'ArrowLeft') setLightboxIdx(i => (i > 0 ? i - 1 : i));
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightboxIdx, images.length, closeLightbox]);

  const scrollToMap = useCallback(() => {
    const el = document.getElementById('detail-map');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const shareUrl = property
    ? `https://mfrealstate.com.br${MF_BASE}/imoveis/${buildPropertySlug(property)}`
    : `https://mfrealstate.com.br${MF_BASE}/imoveis/${id}`;
  const shareTitle = property?.titulo || 'Imóvel MF Real Estate';

  const handleShare = useCallback(() => setShareOpen(true), []);

  const shareViaWhatsapp = () => {
    const txt = `${shareTitle}\n${shareUrl}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(txt)}`, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };
  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
    setShareOpen(false);
  };
  const shareViaTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
    setShareOpen(false);
  };
  const shareViaLinkedin = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
    setShareOpen(false);
  };
  const shareViaEmail = () => {
    const subject = `Veja este imóvel: ${shareTitle}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareUrl)}`;
    setShareOpen(false);
  };
  const shareCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 1800);
    } catch {
      window.prompt('Copie o link:', shareUrl);
    }
    setShareOpen(false);
  };

  // Nearby places (Overpass) — DECLARADOS ANTES DOS EARLY RETURNS (rules of hooks)
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState(null);
  const [nearbyVisibility, setNearbyVisibility] = useState(new Map());
  const [openCategories, setOpenCategories] = useState(new Set(['mercados', 'shopping', 'restaurantes']));

  const [corretoresVinculados, setCorretoresVinculados] = useState([]);

  useEffect(() => {
    if (!property?.corretor_id) { setCorretorDoImovel(null); return; }
    let canceled = false;
    supabase
      .from('bianca_realestate_corretores')
      .select('id, nome, email, telefone, creci, creci_uf, foto_url, bio')
      .eq('id', property.corretor_id)
      .maybeSingle()
      .then(({ data }) => { if (!canceled) setCorretorDoImovel(data || null); });
    return () => { canceled = true; };
  }, [property?.corretor_id]);

  useEffect(() => {
    if (property?.corretor_id || !property?.id) { setCorretoresVinculados([]); return; }
    let canceled = false;
    getCorretoresDoImovel(property.id).then(list => {
      if (!canceled) setCorretoresVinculados(list);
    });
    return () => { canceled = true; };
  }, [property?.id, property?.corretor_id]);

  useEffect(() => {
    const coords = property ? getPropertyCoords(property) : null;
    if (!coords) return;
    let canceled = false;
    setNearbyLoading(true);
    setNearbyError(null);
    fetchNearbyPlaces(coords.lat, coords.lng, 10000)
      .then(data => {
        if (canceled) return;
        setNearbyPlaces(data);
        const vis = new Map();
        for (const p of data) {
          if (!vis.has(p.category)) vis.set(p.category, true);
        }
        setNearbyVisibility(vis);
      })
      .catch(e => { if (!canceled) setNearbyError(e.message || String(e)); })
      .finally(() => { if (!canceled) setNearbyLoading(false); });
    return () => { canceled = true; };
  }, [property?.id, property?.latitude, property?.longitude]);

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <Container style={{ paddingTop: 120, textAlign: 'center', minHeight: '60vh' }}>
          <BodyText $muted>Carregando...</BodyText>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  if (!property) {
    return (
      <PageContainer>
        <Navbar />
        <Container style={{ paddingTop: 120, textAlign: 'center', minHeight: '60vh' }}>
          <HeadingSerif $size="md">Imóvel não encontrado</HeadingSerif>
          <ButtonOutline onClick={() => navigate(`${MF_BASE}/imoveis`)}>
            Ver todos os imóveis
          </ButtonOutline>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  const hasVenda = !!property.preco;
  const hasAluguel = !!property.preco_aluguel;
  const showTabs = hasVenda && hasAluguel;

  const toggleNearbyVisibility = (catId) => {
    setNearbyVisibility(prev => {
      const next = new Map(prev);
      next.set(catId, !(next.get(catId) ?? true));
      return next;
    });
  };
  const toggleOpenCategory = (catId) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // Filtra os places que estão visíveis pra passar pro mapa
  const visibleNearbyPlaces = nearbyPlaces.filter(p => nearbyVisibility.get(p.category) !== false);

  // Calculadora — preenche valores default e calcula parcela
  const mortgagePrecoNum = Number(String(mortgage.preco || property.preco || '').replace(/\D/g, '')) || 0;
  const mortgageEntradaNum = Number(String(mortgage.entrada || '').replace(/\D/g, '')) || Math.round(mortgagePrecoNum * 0.2);
  const mortgageMeses = Number(mortgage.meses) || 360;
  const mortgageTaxaMes = (Number(mortgage.juros) || 11) / 100 / 12;
  const mortgageFinanciado = Math.max(0, mortgagePrecoNum - mortgageEntradaNum);
  let mortgageParcela = '—';
  let mortgageTotalPago = '—';
  let mortgageJurosTotal = '—';
  if (mortgageFinanciado > 0 && mortgageMeses > 0 && mortgageTaxaMes > 0) {
    const fator = Math.pow(1 + mortgageTaxaMes, mortgageMeses);
    const parcela = mortgageFinanciado * (mortgageTaxaMes * fator) / (fator - 1);
    const totalPago = parcela * mortgageMeses;
    const jurosTotal = totalPago - mortgageFinanciado;
    const fmt = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    mortgageParcela = fmt(parcela);
    mortgageTotalPago = fmt(totalPago);
    mortgageJurosTotal = fmt(jurosTotal);
  }
  const activeTab = !hasVenda && hasAluguel ? 'aluguel' : (!hasAluguel && hasVenda ? 'venda' : negocioTab);
  const activePrice = activeTab === 'aluguel' ? property.preco_aluguel : property.preco;
  const activeCond = activeTab === 'aluguel' ? property.preco_condominio_aluguel : property.preco_condominio;
  const activeIptu = activeTab === 'aluguel' ? property.iptu_aluguel : property.iptu;

  const pricePerM2 = activePrice && property.area_m2
    ? formatPrice(Math.round(activePrice / property.area_m2))
    : null;

  const destPhone = leadPhone || property.corretor_telefone || '5511999999999';
  const visitaUrl = `${MF_BASE}/visita/agendar/${property.id}`;
  const whatsappInfoUrl = `https://wa.me/${destPhone}?text=${encodeURIComponent(
    `Olá! Quero mais informações sobre o imóvel: ${property.titulo}`
  )}`;

  // Fotos visiveis na galeria (2 por página, estilo Quinto Andar)
  const photoLeft = images[mosaicStart] || images[0];
  const photoRight = images[mosaicStart + 1];

  // Contagem de fotos vs vídeos (para os pills "Fotos | Vídeos")
  const photoCount = images.filter(u => !isVideo(u)).length;
  const videoCount = images.filter(u => isVideo(u)).length;
  const firstVideoIdx = images.findIndex(u => isVideo(u));
  const firstPhotoIdx = images.findIndex(u => !isVideo(u));

  return (
    <PageContainer>
      <Navbar />

      {/* Mobile hero: carousel swipe-able + titulo sobreposto + pills */}
      <MobileHero>
        <MobileHeroTrack
          ref={mobileTrackRef}
          onScroll={handleMobileScroll}
        >
          {images.map((src, i) => (
            <MobileHeroSlide key={i} onClick={() => openLightbox(i)}>
              <Media
                src={src}
                alt={`${property.titulo} - foto ${i + 1}`}
                draggable={false}
                optimizeWidth={800}
              />
            </MobileHeroSlide>
          ))}
        </MobileHeroTrack>
        <MobileHeroGradient />
        <MobileHeroTitle>{property.titulo}</MobileHeroTitle>
        {images.length > 1 && images.length <= 8 && (
          <MobileHeroDots>
            {images.map((_, i) => (
              <MobileHeroDot key={i} $active={i === mobileIdx} />
            ))}
          </MobileHeroDots>
        )}
        {images.length > 8 && (
          <MobileHeroCounter>
            <strong>{mobileIdx + 1}</strong> / {images.length}
          </MobileHeroCounter>
        )}
        <MobileHeroPills>
          {photoCount > 0 && (
            <MosaicPill onClick={() => openLightbox(firstPhotoIdx >= 0 ? firstPhotoIdx : 0)} type="button">
              <FiImage /> {photoCount} {photoCount === 1 ? 'Foto' : 'Fotos'}
            </MosaicPill>
          )}
          {videoCount > 0 && (
            <MosaicPill onClick={() => openLightbox(firstVideoIdx)} type="button">
              <FiPlay /> {videoCount} {videoCount === 1 ? 'Vídeo' : 'Vídeos'}
            </MosaicPill>
          )}
          <MosaicPill onClick={scrollToMap} type="button">
            <FiMapPin /> Mapa
          </MosaicPill>
        </MobileHeroPills>
      </MobileHero>

      {/* Mobile price card abaixo da foto */}
      <MobilePriceCard>
        {property.created_at && (
          <PublishedMeta style={{ marginBottom: 4 }}>{formatPublishedAgo(property.created_at)}</PublishedMeta>
        )}
        <MobilePriceCardTitle>{property.titulo}</MobilePriceCardTitle>
        {showTabs && (
          <NegocioTabs activeTab={activeTab} onChange={setNegocioTab} />
        )}
        <MobilePriceLine>
          <div className="label">{activeTab === 'aluguel' ? 'Aluguel' : 'Valor'}</div>
          <div className="value">{formatPriceOrConsult(activePrice)}{activeTab === 'aluguel' && activePrice ? '/mês' : ''}</div>
          {property.area_m2 && <div className="sub">{formatArea(property.area_m2)}</div>}
        </MobilePriceLine>

        {/* Tabela de valores — só aparece se tiver algum custo adicional */}
        {(activeCond || activeIptu) && (
          <ValuesTable style={{ width: '100%' }}>
            {activePrice && (
              <ValuesRow>
                <span className="val-label">{activeTab === 'aluguel' ? 'Aluguel' : 'Valor'}</span>
                <span className="val-amount">{formatPrice(activePrice)}</span>
              </ValuesRow>
            )}
            {activeCond && (
              <ValuesRow>
                <span className="val-label">Condomínio</span>
                <span className="val-amount">{formatPrice(activeCond)}</span>
              </ValuesRow>
            )}
            {activeIptu && (
              <ValuesRow>
                <span className="val-label">IPTU</span>
                <span className="val-amount">{formatPrice(activeIptu)}</span>
              </ValuesRow>
            )}
            {activeTab === 'aluguel' && activePrice && (activeCond || activeIptu) && (
              <ValuesRow className="total">
                <span className="val-label">Total</span>
                <span className="val-amount">
                  {formatPrice(
                    Number(activePrice || 0) +
                    Number(activeCond || 0) +
                    Number(activeIptu || 0)
                  )}
                </span>
              </ValuesRow>
            )}
          </ValuesTable>
        )}

        <ButtonSolid
          as={Link}
          to={visitaUrl}
          style={{ width: '100%' }}
        >
          Agendar Visita
        </ButtonSolid>
        <ButtonOutline
          as="a"
          href={whatsappInfoUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ width: '100%' }}
        >
          Quero mais informações
        </ButtonOutline>
        <SideActions style={{ width: '100%' }}>
          <SideActionButton type="button" onClick={handleShare}>
            <FiShare2 /> Compartilhar
          </SideActionButton>
        </SideActions>
      </MobilePriceCard>

      <Container style={{ paddingTop: 20, paddingBottom: 80 }}>
        {/* Hero full-width: galeria mosaico 5 fotos + thumbnail strip */}
        <DetailHero>
          <GalleryBlock>
            <GalleryMosaic>
              {/* Big à esquerda (foto principal, span 2 linhas) */}
              <MosaicBigSpan onClick={() => openLightbox(0)} type="button">
                {images[0] && <Media src={images[0]} alt={`${property.titulo} - foto 1`} optimizeWidth={900} />}
              </MosaicBigSpan>
              {/* 4 tiles menores à direita (2x2) */}
              {[1, 2, 3, 4].map((idx) => {
                const isLast = idx === 4;
                const remaining = images.length - 5;
                if (!images[idx]) {
                  return <MosaicSmall key={idx} type="button" style={{ visibility: 'hidden' }} />;
                }
                if (isLast && remaining > 0) {
                  return (
                    <MosaicMore key={idx} onClick={() => openLightbox(5)} type="button">
                      <Media src={images[idx]} alt={`${property.titulo} - foto ${idx + 1}`} optimizeWidth={500} />
                      <span className="overlay">+ {remaining} foto{remaining > 1 ? 's' : ''}</span>
                    </MosaicMore>
                  );
                }
                return (
                  <MosaicSmall key={idx} onClick={() => openLightbox(idx)} type="button">
                    <Media src={images[idx]} alt={`${property.titulo} - foto ${idx + 1}`} optimizeWidth={500} />
                  </MosaicSmall>
                );
              })}

              <MosaicPills>
                {photoCount > 0 && (
                  <MosaicPill onClick={() => openLightbox(firstPhotoIdx >= 0 ? firstPhotoIdx : 0)} type="button">
                    <FiImage /> {photoCount} {photoCount === 1 ? 'Foto' : 'Fotos'}
                  </MosaicPill>
                )}
                {videoCount > 0 && (
                  <MosaicPill onClick={() => openLightbox(firstVideoIdx)} type="button">
                    <FiPlay /> {videoCount} {videoCount === 1 ? 'Vídeo' : 'Vídeos'}
                  </MosaicPill>
                )}
                <MosaicPill onClick={scrollToMap} type="button">
                  <FiMapPin /> Mapa
                </MosaicPill>
              </MosaicPills>
            </GalleryMosaic>


            <GalleryShare onClick={handleShare} type="button" aria-label="Copiar link do imóvel">
              <FiShare2 />
            </GalleryShare>
            {shareToast && <ShareToast>Link copiado</ShareToast>}
          </GalleryBlock>
        </DetailHero>

        {/* Breadcrumbs + Endereco */}
        <Breadcrumbs style={{ marginTop: 32 }}>
          <a href={MF_BASE || '/'} onClick={(e) => { e.preventDefault(); navigate(MF_BASE || '/'); }}>
            MF Real Estate
          </a>
          <span>/</span>
          <a href={`${MF_BASE}/imoveis`} onClick={(e) => { e.preventDefault(); navigate(`${MF_BASE}/imoveis`); }}>
            Imóveis
          </a>
          <span>/</span>
          {property.bairro}
        </Breadcrumbs>

        <DetailGrid>
          {/* Coluna principal */}
          <div>
            {/* Card endereço — mini-mapa no fundo, clica e scrolla pro mapa completo */}
            {/* Header: pre + título + endereço + badges */}
            <ListingHeader>
              <ListingPre>
                {[property.bairro, property.cidade].filter(Boolean).join(' · ')}
              </ListingPre>
              <ListingTitle>{property.titulo}</ListingTitle>
              <ListingAddress>
                <FiMapPin />
                {[property.endereco, property.condominio, property.cidade].filter(Boolean).join(' · ')}
              </ListingAddress>
              <ListingBadges>
                {property.destaque && <ListingBadge $gold>★ Destaque</ListingBadge>}
                {property.preco > 0 && <ListingBadge>À venda</ListingBadge>}
                {property.preco_aluguel > 0 && <ListingBadge>Para alugar</ListingBadge>}
                {property.mobiliado && <ListingBadge>Mobiliado</ListingBadge>}
              </ListingBadges>
            </ListingHeader>

            {/* Código + publicado */}
            <CodeMetaRow>
              <CodeChip>Imóvel #{property.id.slice(0, 8).toUpperCase()}</CodeChip>
              {property.created_at && (
                <MetaInline>{formatPublishedAgo(property.created_at)}</MetaInline>
              )}
            </CodeMetaRow>

            {/* Descrição com "Ver mais" */}
            {property.descricao && (() => {
              const isHtml = /<[a-z][\s\S]*>/i.test(property.descricao);
              const safeHtml = isHtml ? DOMPurify.sanitize(property.descricao, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 's', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote'],
                ALLOWED_ATTR: ['href', 'target', 'rel'],
              }) : '';
              const plainLength = property.descricao.replace(/<[^>]+>/g, '').length;
              return (
                <DescriptionBlock $expanded={descExpanded} style={{ marginTop: 40 }}>
                  <PreTitle>Sobre o imóvel</PreTitle>
                  <SectionTitle>Descrição</SectionTitle>
                  {isHtml
                    ? <div className="desc-text desc-rich" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                    : <div className="desc-text">{property.descricao}</div>
                  }
                  {plainLength > 280 && (
                    <button
                      className="desc-toggle"
                      type="button"
                      onClick={() => setDescExpanded(v => !v)}
                    >
                      {descExpanded ? 'Ver menos' : 'Ver mais'}
                    </button>
                  )}
                </DescriptionBlock>
              );
            })()}

            {/* Overview — grid de stats com ícones dourados */}
            <div style={{ marginTop: 40 }}>
              <PreTitle>Visão geral</PreTitle>
              <SectionTitle>Overview</SectionTitle>
              <OverviewGrid>
                <OverviewCell>
                  <span className="ic"><FiHome /></span>
                  <div>
                    <div className="lbl">Tipo</div>
                    <div className="val" style={{ textTransform: 'capitalize' }}>{property.tipo || '—'}</div>
                  </div>
                </OverviewCell>
                {property.area_construida_m2 && (
                  <OverviewCell>
                    <span className="ic"><TbBuildingSkyscraper /></span>
                    <div>
                      <div className="lbl">Construída</div>
                      <div className="val">{property.area_construida_m2} m²</div>
                    </div>
                  </OverviewCell>
                )}
                {property.area_m2 && (
                  <OverviewCell>
                    <span className="ic"><FiMaximize /></span>
                    <div>
                      <div className="lbl">Terreno</div>
                      <div className="val">{property.area_m2} m²</div>
                    </div>
                  </OverviewCell>
                )}
                {property.quartos && (
                  <OverviewCell>
                    <span className="ic"><FiHome /></span>
                    <div>
                      <div className="lbl">Quartos</div>
                      <div className="val">
                        {property.quartos}
                        {property.suites > 0 ? ` (${property.suites} suítes)` : ''}
                      </div>
                    </div>
                  </OverviewCell>
                )}
                {property.banheiros && (
                  <OverviewCell>
                    <span className="ic"><FiDroplet /></span>
                    <div>
                      <div className="lbl">Banheiros</div>
                      <div className="val">{property.banheiros}</div>
                    </div>
                  </OverviewCell>
                )}
                {property.vagas && (
                  <OverviewCell>
                    <span className="ic"><FiTruck /></span>
                    <div>
                      <div className="lbl">Vagas</div>
                      <div className="val">{property.vagas}</div>
                    </div>
                  </OverviewCell>
                )}
                {property.andar && (
                  <OverviewCell>
                    <span className="ic"><FiLayers /></span>
                    <div>
                      <div className="lbl">Andar</div>
                      <div className="val">{property.andar}º</div>
                    </div>
                  </OverviewCell>
                )}
                {property.mobiliado && (
                  <OverviewCell>
                    <span className="ic"><FiPackage /></span>
                    <div>
                      <div className="lbl">Mobília</div>
                      <div className="val">Mobiliado</div>
                    </div>
                  </OverviewCell>
                )}
              </OverviewGrid>
            </div>

            {/* Endereço — card com Google Maps */}
            <div style={{ marginTop: 40 }}>
              <PreTitle>Onde fica</PreTitle>
              <SectionTitle>Endereço</SectionTitle>
              <AddressV2>
                <AddressV2Head>
                  <span className="hint">Confira a localização completa</span>
                  {(() => {
                    const coords = getPropertyCoords(property);
                    const q = coords
                      ? `${coords.lat},${coords.lng}`
                      : [property.endereco, property.numero, property.bairro, property.cidade, 'Brasil'].filter(Boolean).join(', ');
                    return (
                      <AddressV2MapBtn
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir no Google Maps →
                      </AddressV2MapBtn>
                    );
                  })()}
                </AddressV2Head>
                <AddressV2Grid>
                  {property.endereco && (
                    <div className="row"><span className="k">Endereço</span><span className="v">{property.endereco}</span></div>
                  )}
                  {property.bairro && (
                    <div className="row"><span className="k">Bairro</span><span className="v">{property.bairro}</span></div>
                  )}
                  {property.cidade && (
                    <div className="row"><span className="k">Cidade</span><span className="v">{property.cidade}</span></div>
                  )}
                  {property.cep && (
                    <div className="row"><span className="k">CEP</span><span className="v">{property.cep}</span></div>
                  )}
                  {property.condominio && (
                    <div className="row"><span className="k">Condomínio</span><span className="v">{property.condominio}</span></div>
                  )}
                </AddressV2Grid>
              </AddressV2>
            </div>

            {/* Detalhes — ficha técnica 2-col */}
            <div style={{ marginTop: 40 }}>
              <PreTitle>Ficha técnica</PreTitle>
              <SectionTitle>Detalhes</SectionTitle>
              <DetailsBox>
                <DetailsRow><span className="k">Código</span><span className="v">#{property.id.slice(0, 8).toUpperCase()}</span></DetailsRow>
                <DetailsRow><span className="k">Tipo</span><span className="v" style={{ textTransform: 'capitalize' }}>{property.tipo || '—'}</span></DetailsRow>
                {property.preco > 0 && (
                  <DetailsRow><span className="k">Preço de venda</span><span className="v">{formatPrice(property.preco)}</span></DetailsRow>
                )}
                {property.preco_aluguel > 0 && (
                  <DetailsRow><span className="k">Aluguel</span><span className="v">{formatPrice(property.preco_aluguel)}/mês</span></DetailsRow>
                )}
                {property.area_m2 && (
                  <DetailsRow><span className="k">Área terreno</span><span className="v">{property.area_m2} m²</span></DetailsRow>
                )}
                {property.area_construida_m2 && (
                  <DetailsRow><span className="k">Área construída</span><span className="v">{property.area_construida_m2} m²</span></DetailsRow>
                )}
                {property.quartos && (
                  <DetailsRow><span className="k">Quartos</span><span className="v">{property.quartos}{property.suites ? ` (${property.suites} suítes)` : ''}</span></DetailsRow>
                )}
                {property.banheiros && (
                  <DetailsRow><span className="k">Banheiros</span><span className="v">{property.banheiros}</span></DetailsRow>
                )}
                {property.vagas && (
                  <DetailsRow><span className="k">Vagas</span><span className="v">{property.vagas}</span></DetailsRow>
                )}
                {property.preco_condominio > 0 && (
                  <DetailsRow><span className="k">Condomínio</span><span className="v">{formatPrice(property.preco_condominio)}/mês</span></DetailsRow>
                )}
                {property.iptu > 0 && (
                  <DetailsRow><span className="k">IPTU</span><span className="v">{formatPrice(property.iptu)}/mês</span></DetailsRow>
                )}
                <DetailsRow><span className="k">Status</span><span className="v" style={{ textTransform: 'capitalize' }}>{property.status || '—'}</span></DetailsRow>
              </DetailsBox>
            </div>

            {/* Plantas baixas — accordion */}
            {property.plantas?.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <PreTitle>Pavimentos</PreTitle>
                <SectionTitle>Planta baixa</SectionTitle>
                <FloorsAccordion>
                  {property.plantas.map((p, i) => {
                    const isOpen = openFloor === i;
                    return (
                      <Floor key={i}>
                        <FloorHead
                          type="button"
                          $open={isOpen}
                          onClick={() => setOpenFloor(isOpen ? -1 : i)}
                        >
                          <span className="floor-name">{p.nome || `Piso ${i + 1}`}</span>
                          {p.area
                            ? <span className="floor-stat"><span className="k">Área:</span><span className="v">{p.area} m²</span></span>
                            : <span />}
                          {p.quartos
                            ? <span className="floor-stat"><span className="k">Quartos:</span><span className="v">{p.quartos}</span></span>
                            : <span />}
                          {p.banheiros
                            ? <span className="floor-stat"><span className="k">Banheiros:</span><span className="v">{p.banheiros}</span></span>
                            : <span />}
                          {p.preco
                            ? <span className="floor-stat price"><span className="k">Preço:</span><span className="v">{formatPrice(p.preco)}</span></span>
                            : <span />}
                          <span className="chev">▼</span>
                        </FloorHead>
                        <FloorBody $open={isOpen}>
                          <FloorPlanImg
                            $src={p.imagem}
                            $empty={!p.imagem}
                            onClick={() => p.imagem && setLightboxSrc(p.imagem)}
                          >
                            {p.imagem
                              ? <span className="zoom-icon">⤢</span>
                              : 'SEM IMAGEM CADASTRADA'}
                          </FloorPlanImg>
                          {p.descricao && <FloorDesc>{p.descricao}</FloorDesc>}
                        </FloorBody>
                      </Floor>
                    );
                  })}
                </FloorsAccordion>
              </div>
            )}


            {/* Características */}
            {property.amenidades?.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <PreTitle>Comodidades</PreTitle>
                <SectionTitle>Características</SectionTitle>
                <FeaturesGrid3>
                  {property.amenidades.map((a, i) => (
                    <FeatureItem key={i}>
                      <span className="check"><FiCheck /></span>
                      {a}
                    </FeatureItem>
                  ))}
                </FeaturesGrid3>
              </div>
            )}

            {/* Proximidades — Overpass API */}
            {getPropertyCoords(property) && (
              <div style={{ marginTop: 48 }}>
                <PreTitle>O que tem perto</PreTitle>
                <SectionTitle>Proximidades</SectionTitle>
                <NearbyPlaces
                  places={nearbyPlaces}
                  loading={nearbyLoading}
                  error={nearbyError}
                  visibility={nearbyVisibility}
                  onToggleVisibility={toggleNearbyVisibility}
                  openCategories={openCategories}
                  onToggleOpen={toggleOpenCategory}
                />
              </div>
            )}

            {/* Mapa */}
            <div id="detail-map" style={{ marginTop: 48 }}>
              <PreTitle>Mapa</PreTitle>
              <SectionTitle>Localização</SectionTitle>
              <MapWrapper>
                <PropertyMap properties={[property]} nearbyPlaces={visibleNearbyPlaces} />
              </MapWrapper>
            </div>
          </div>

          {/* Sidebar CTA (desktop, complementar ao hero) */}
          <CTASidebar>
            {showTabs && (
              <NegocioTabs activeTab={activeTab} onChange={setNegocioTab} />
            )}
            <PriceDisplay>
              <div className="price-main">{formatPriceOrConsult(activePrice)}{activeTab === 'aluguel' && activePrice ? '/mês' : ''}</div>
              {property.area_m2 && <div className="price-meter">{formatArea(property.area_m2)}</div>}
            </PriceDisplay>

            {/* Tabela de valores — só aparece se tiver algum custo adicional */}
            {(activeCond || activeIptu) && (
              <ValuesTable>
                {activePrice && (
                  <ValuesRow>
                    <span className="val-label">{activeTab === 'aluguel' ? 'Aluguel' : 'Valor'}</span>
                    <span className="val-amount">{formatPrice(activePrice)}</span>
                  </ValuesRow>
                )}
                {activeCond && (
                  <ValuesRow>
                    <span className="val-label">Condomínio</span>
                    <span className="val-amount">{formatPrice(activeCond)}</span>
                  </ValuesRow>
                )}
                {activeIptu && (
                  <ValuesRow>
                    <span className="val-label">IPTU</span>
                    <span className="val-amount">{formatPrice(activeIptu)}</span>
                  </ValuesRow>
                )}
                {activeTab === 'aluguel' && activePrice && (activeCond || activeIptu) && (
                  <ValuesRow className="total">
                    <span className="val-label">Total</span>
                    <span className="val-amount">
                      {formatPrice(
                        Number(activePrice || 0) +
                        Number(activeCond || 0) +
                        Number(activeIptu || 0)
                      )}
                    </span>
                  </ValuesRow>
                )}
              </ValuesTable>
            )}

            {(() => {
              const corretorUnico = property.corretor_id
                ? corretorDoImovel
                : corretoresVinculados.length === 1 ? corretoresVinculados[0] : null;
              const corretorNomeFallback = property.corretor_id ? property.corretor_nome : null;
              if (!corretorUnico && !corretorNomeFallback) return null;
              const cor = corretorUnico;
              const corretorPhone = cor?.telefone || property.corretor_telefone;
              const corretorWhatsapp = corretorPhone
                ? `https://wa.me/${corretorPhone.replace(/\D/g, '')}?text=${encodeURIComponent(
                    `Olá! Vi o imóvel "${property.titulo}" no site da MF Real Estate e gostaria de mais informações.`
                  )}`
                : null;
              return (
                <CorretorCard>
                  {cor?.foto_url ? (
                    <img src={cor.foto_url} alt={cor.nome} />
                  ) : (
                    <div className="avatar-fallback">
                      {(cor?.nome || corretorNomeFallback || '?').trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="info">
                    <div className="label">Atendimento</div>
                    <div className="name">{cor?.nome || corretorNomeFallback}</div>
                    {cor?.creci && (
                      <div className="creci">
                        CRECI {cor.creci}{cor.creci_uf ? `-${cor.creci_uf}` : ''}
                      </div>
                    )}
                  </div>
                  {corretorWhatsapp && (
                    <a
                      className="whatsapp-btn"
                      href={corretorWhatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="WhatsApp do corretor"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                  )}
                </CorretorCard>
              );
            })()}

            <ButtonSolid
              $fullWidth
              as={Link}
              to={visitaUrl}
            >
              Agendar Visita
            </ButtonSolid>
            <ButtonOutline
              $fullWidth
              as="a"
              href={whatsappInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Quero mais informações
            </ButtonOutline>
            <SideActions>
              <SideActionButton type="button" onClick={handleShare}>
                <FiShare2 /> Compartilhar
              </SideActionButton>
            </SideActions>

            {/* Calculadora de financiamento (sidebar) */}
            {Number(property.preco) > 0 && (
              <MortgageSide>
                <h3>Simular financiamento</h3>
                <label>
                  <span className="label">Valor do imóvel</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={mortgage.preco || formatPrice(property.preco)}
                    onChange={onMoneyChange('preco')}
                  />
                </label>
                <div className="row">
                  <label>
                    <span className="label">Entrada</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={mortgage.entrada}
                      placeholder="20% padrão"
                      onChange={onMoneyChange('entrada')}
                    />
                  </label>
                  <label>
                    <span className="label">Prazo (meses)</span>
                    <input
                      type="text"
                      value={mortgage.meses}
                      onChange={(e) => setMortgageField('meses', e.target.value)}
                    />
                  </label>
                </div>
                <label>
                  <span className="label">Juros ao ano (%)</span>
                  <input
                    type="text"
                    value={mortgage.juros}
                    onChange={(e) => setMortgageField('juros', e.target.value)}
                  />
                </label>
                <div className="result">
                  <div className="lbl">Parcela mensal estimada</div>
                  <div className="val">{mortgageParcela}</div>
                  <div className="breakdown">
                    <div className="line"><span>Total a pagar</span><b>{mortgageTotalPago}</b></div>
                    <div className="line"><span>Total de juros</span><b>{mortgageJurosTotal}</b></div>
                  </div>
                  <div className="hint">Tabela Price · cálculo indicativo. Banco oferece taxas e prazos diferentes.</div>
                </div>
              </MortgageSide>
            )}
          </CTASidebar>
        </DetailGrid>

        {/* Similares */}
        {similar.length > 0 && (
          <Section>
            <PreTitle>Você também pode gostar</PreTitle>
            <HeadingSerif $size="md">Imóveis similares</HeadingSerif>
            <PropertyGrid>
              {similar.map(p => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </PropertyGrid>
          </Section>
        )}
      </Container>

      {/* Lightbox fullscreen */}
      {lightboxIdx !== null && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxHeader onClick={e => e.stopPropagation()}>
            <span>{lightboxIdx + 1} / {images.length}</span>
            <button type="button" onClick={closeLightbox}>Fechar</button>
          </LightboxHeader>
          <LightboxContent onClick={e => e.stopPropagation()}>
            <LightboxBody>
              <Media
                src={images[lightboxIdx]}
                alt={`Foto ${lightboxIdx + 1}`}
                controls
              />
              {lightboxIdx > 0 && (
                <LightboxNav
                  $dir="left"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
                  type="button"
                >
                  ‹
                </LightboxNav>
              )}
              {lightboxIdx < images.length - 1 && (
                <LightboxNav
                  $dir="right"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
                  type="button"
                >
                  ›
                </LightboxNav>
              )}
            </LightboxBody>
            <LightboxThumbs>
              {images.map((src, i) => (
                <LightboxThumb
                  key={i}
                  $active={i === lightboxIdx}
                  onClick={() => setLightboxIdx(i)}
                >
                  <img src={optimizedImageUrl(src, { width: 150 })} alt={`${property.titulo} - foto ${i + 1}`} />
                </LightboxThumb>
              ))}
            </LightboxThumbs>
          </LightboxContent>
        </LightboxOverlay>
      )}

      {/* Lightbox dedicado pra planta baixa */}
      <FloorPlanLightbox $open={!!lightboxSrc} onClick={() => setLightboxSrc(null)}>
        <button className="close" type="button" onClick={() => setLightboxSrc(null)}>×</button>
        {lightboxSrc && <img src={lightboxSrc} alt={`${property.titulo} - planta baixa`} onClick={(e) => e.stopPropagation()} />}
      </FloorPlanLightbox>

      {shareOpen && (
        <ShareOverlay onClick={() => setShareOpen(false)}>
          <ShareCard onClick={(e) => e.stopPropagation()}>
            <ShareClose type="button" onClick={() => setShareOpen(false)} aria-label="Fechar">
              <FiX />
            </ShareClose>
            <ShareTitle>Compartilhar imóvel</ShareTitle>
            <ShareSubtitle>{shareTitle}</ShareSubtitle>
            <ShareGrid>
              <ShareOption type="button" onClick={shareViaWhatsapp}>
                <span className="ico" style={{ color: '#25D366' }}><FaWhatsapp /></span>
                <span>WhatsApp</span>
              </ShareOption>
              <ShareOption type="button" onClick={shareViaFacebook}>
                <span className="ico" style={{ color: '#1877F2' }}><FaFacebookF /></span>
                <span>Facebook</span>
              </ShareOption>
              <ShareOption type="button" onClick={shareViaTwitter}>
                <span className="ico"><FaXTwitter /></span>
                <span>X / Twitter</span>
              </ShareOption>
              <ShareOption type="button" onClick={shareViaLinkedin}>
                <span className="ico" style={{ color: '#0A66C2' }}><FaLinkedinIn /></span>
                <span>LinkedIn</span>
              </ShareOption>
              <ShareOption type="button" onClick={shareViaEmail}>
                <span className="ico"><FiMail /></span>
                <span>E-mail</span>
              </ShareOption>
              <ShareOption type="button" onClick={shareCopyLink}>
                <span className="ico"><FiLink /></span>
                <span>Copiar link</span>
              </ShareOption>
            </ShareGrid>
          </ShareCard>
        </ShareOverlay>
      )}

      <Footer />
    </PageContainer>
  );
}

const CorretorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 14px;
  background: rgba(184, 147, 90, .06);
  border: 1px solid rgba(184, 147, 90, .2);
  border-radius: 10px;
  > img, > .avatar-fallback {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }
  .avatar-fallback {
    background: #B8935A;
    color: #0F1828;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
  }
  .info { min-width: 0; }
  .label {
    font-size: 9px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: rgba(245, 241, 232, .5);
    margin-bottom: 2px;
  }
  .name {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 17px;
    font-weight: 500;
    color: #F5F1E8;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .creci {
    font-size: 11px;
    color: rgba(245, 241, 232, .55);
    margin-top: 1px;
  }
  .whatsapp-btn {
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(37, 211, 102, .15);
    color: #25D366;
    transition: background .2s, transform .15s;
    &:hover { background: rgba(37, 211, 102, .3); transform: scale(1.08); }
    svg { width: 20px; height: 20px; }
  }
`;

const ShareOverlay = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(15, 24, 40, 0.78);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: shFade .15s ease-out;
  @keyframes shFade { from { opacity: 0; } to { opacity: 1; } }
`;
const ShareCard = styled.div`
  position: relative;
  width: 100%; max-width: 460px;
  background: #0F1828;
  border: 1px solid rgba(184, 147, 90, .25);
  border-radius: 14px;
  padding: 32px 28px 28px;
  color: #F5F1E8;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .5);
`;
const ShareClose = styled.button`
  position: absolute; top: 14px; right: 14px;
  background: transparent; border: none; color: rgba(245, 241, 232, .6);
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 18px;
  &:hover { background: rgba(255, 255, 255, .05); color: #F5F1E8; }
`;
const ShareTitle = styled.h3`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 24px; font-weight: 500; margin: 0 0 6px;
`;
const ShareSubtitle = styled.p`
  font-size: 13px; color: rgba(245, 241, 232, .6);
  margin: 0 0 22px; line-height: 1.4;
`;
const ShareGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 440px) { grid-template-columns: repeat(2, 1fr); }
`;
const ShareOption = styled.button`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 16px 8px;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(184, 147, 90, .18);
  border-radius: 10px;
  color: #F5F1E8;
  font-size: 12px; font-family: inherit;
  cursor: pointer;
  transition: border-color .15s, background .15s, transform .1s;
  .ico {
    font-size: 22px;
    display: flex; align-items: center; justify-content: center;
    width: 40px; height: 40px;
    color: #D4A56A;
  }
  &:hover {
    border-color: #B8935A;
    background: rgba(184, 147, 90, .08);
    transform: translateY(-1px);
  }
`;
